import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { useIntl } from "react-intl";

import { ReactComponent as AttachmentIcon } from "../../icons/attachment.svg";
import VoiceInput, { VoiceInputHandle } from "../VoiceInput/VoiceInput";
import ChatInputField from "./ChatInputField";
import ActionButton from "./ActionButton";
import { translateMessage } from "src/app/methods/translateMessage";
import { ALLOWED_MIMES, MAX_FILES } from "../../utils/fileUpload";
import { useFileAttachments } from "./useFileAttachments";
import { resizeTextarea } from "./resizeTextarea";

import "./ChatInput.scss";

interface ChatInputProps {
  onSend: (message: string, files: File[]) => Promise<boolean>;
  onAbort: () => void;
  disabled: boolean;
  isArchived: boolean;
  isUploading: boolean;
  isOpen: boolean;
}

export default function ChatInput({
  onSend,
  onAbort,
  disabled,
  isArchived,
  isUploading,
  isOpen,
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const voiceInputRef = useRef<VoiceInputHandle>(null);

  const intl = useIntl();
  const inputsDisabled = disabled || isUploading;

  const {
    files,
    fileInputRef,
    handleFileClick,
    handleFileChange,
    handlePaste,
    handleRemoveFile,
    clearFiles,
    restoreFiles,
  } = useFileAttachments();

  const handleSend = async () => {
    if (!message.trim() || inputsDisabled) return;

    voiceInputRef.current?.stopListening();
    const sentMessage = message.trim();
    const sentFiles = files;

    // Clear immediately; restore if the send fails.
    setMessage("");
    clearFiles();
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    const ok = await onSend(sentMessage, sentFiles);
    if (!ok) {
      setMessage(sentMessage);
      restoreFiles(sentFiles);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (textareaRef.current) resizeTextarea(textareaRef.current);
  };

  const handleTranscript = (text: string) => {
    setMessage((prev) => (prev ? `${prev} ${text}` : text));
    if (textareaRef.current) {
      resizeTextarea(textareaRef.current);
      textareaRef.current.focus();
    }
  };

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const textarea = (
    <textarea
      ref={textareaRef}
      name="chat-input"
      className="chat-input__textarea"
      value={message}
      onChange={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={`${translateMessage({
        intl,
        id: "ws_ask_anything",
        defaultMessage: "Ask anything",
      })} ...`}
      disabled={inputsDisabled}
      rows={1}
    />
  );

  const attachmentButton = (
    <button
      className="chat-input__attachment-btn"
      onClick={handleFileClick}
      disabled={inputsDisabled || files.length >= MAX_FILES}
      type="button"
    >
      <AttachmentIcon className="ai-chat__icon" />
    </button>
  );

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ALLOWED_MIMES.join(",")}
      className="chat-input__file-input hidden"
      onChange={handleFileChange}
    />
  );

  return (
    <div className="chat-input">
      <div className="chat-input__wrapper">
        <ChatInputField files={files} onRemoveFile={handleRemoveFile}>
          {textarea}
        </ChatInputField>
        <div className="chat-input__footer">
          {attachmentButton}
          {fileInput}
          <div className="chat-input__footer-actions">
            <VoiceInput
              ref={voiceInputRef}
              onTranscript={handleTranscript}
              disabled={inputsDisabled}
            />
            <ActionButton
              isUploading={isUploading}
              disabled={disabled}
              isArchived={isArchived}
              canSend={!!message.trim()}
              onSend={handleSend}
              onAbort={onAbort}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
